import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { BsZoomIn } from "react-icons/bs";
import {
  CXTStopTypes,
  dedicatedOrderStatuses,
} from "./DedicatedDispatchConstants";
import { PodModal } from "../Orders/OrderDetailsPanelV2NewEndpoint";
import { FullPageParcelImageModal } from "../Utility/FullPageParcelImageModal";
import { format } from "date-fns";
import { getCDKOrderDetails } from "../../services/cdkDedicatedDispatchService";
import handleApiError from "../../helpers/handleApiError";
import { viewStates } from "../../constants";
import useDecodeToken from "../../hooks/useDecodeToken";
import { isDemoAccountFunc } from "../../services/accountService";
import cdkDemoSignatureFinroy from "../../assets/demo assets/Test signature Finroy.png";

const dummyDetailsData = [
  {
    cdkOrder: null,
    id: 6319325,
    bin1: "53915",
    class: null,
    description: "Transmission",
    employee: "Jake",
    errorLevel: null,
    errorMessage: null,
    extendedCost: 27.76,
    extendedSale: 51.6,
    hostItemID: "953151*3525*2",
    invoiceNumber: "9215862*7351",
    laborOpSeq: "2",
    listPrice: 75.56,
    partNumber: "5K9D*9A353*Z",
    quantity: 1,
    quantitySold: 1,
    referNo: "5915219",
    salesPerson: null,
    seqNo: "1",
    source: "5291",
    specStatus: null,
    unitCoreCost: null,
    unitCoreSalePrice: null,
    unitCost: 25.76,
    unitSalePrice: 70.6,
    unitServiceCharge: null,
    dateTimeAdded: "2025-06-13T14:03:21.025746-05:00",
    cdkOrderId: 591521,
    isImported: false,
  },
];

export const PartNumberDisplay = ({ cdkOrderId }) => {
  const [cdkPartDetails, setCdkPartDetails] = useState([]);
  const [viewState, setViewState] = useState(viewStates.loading);
  const [retryNotif, setRetryNotif] = useState(0);
  const [decodedToken, setDecodedToken] = useDecodeToken();
  const handleClickRetry = (e) => {
    e.preventDefault();
    setRetryNotif((prev) => prev + 1);
  };
  useEffect(() => {
    async function fetchData() {
      try {
        // throw new Error("error");
        setViewState(viewStates.loading);
        if (decodedToken?.nameid?.toLowerCase() === "csrdemo") {
          setCdkPartDetails([...dummyDetailsData]);
        } else {
          const cdkOrderDetailsResponse = await getCDKOrderDetails(cdkOrderId);
          setCdkPartDetails(cdkOrderDetailsResponse.data);
        }
        setViewState(viewStates.success);
      } catch (ex) {
        handleApiError(ex);
        setViewState(viewStates.error);
      }
    }
    fetchData();
  }, [retryNotif]);
  // console.log("cdk order details", cdkPartDetails);
  return (
    <div>
      {viewState === viewStates.loading && (
        <div className="font-600">Loading Part Details...</div>
      )}
      {viewState === viewStates.success && (
        <div>
          <span className="font-600">Part Numbers</span>
          <br />
          <div
            className="panel-flat p-2 mr-5"
            style={{ maxHeight: "200px", overflow: "auto" }}
          >
            {cdkPartDetails?.length > 0 ? (
              cdkPartDetails?.map((partDetails) => {
                return (
                  <div key={partDetails.id}>{partDetails?.partNumber}</div>
                );
              })
            ) : (
              <div className="text-center font-600 text-note">
                No Parts Found
              </div>
            )}
          </div>
        </div>
      )}
      {viewState === viewStates.error && (
        <div className="font-600">Error Retrieving Part Numbers</div>
      )}
    </div>
  );
};

const DedicatedStatusPanel = ({ order, positionName }) => {
  const [decodedToken] = useDecodeToken();
  const isDemoAccountBool = useMemo(
    () => isDemoAccountFunc(decodedToken),
    [decodedToken],
  );
  const originalOrderData = order?.originalData;
  const dateDisplay = useMemo(() => {
    return originalOrderData?.completedTime
      ? format(
          new Date(originalOrderData.completedTime),
          "yyyy-MM-dd | hh:mm a",
        )
      : null;
  }, [originalOrderData]);
  // const signatureSrc = useMemo(() => {
  //   return originalOrderData?.signature
  //     ? base64ToImgSrc(originalOrderData?.signature)
  //     : "";
  // }, [order]);
  const attachmentIDs = useMemo(() => {
    return isDemoAccountBool
      ? ["522519"]
      : originalOrderData.attachments?.trim() === ""
        ? null
        : originalOrderData.attachments?.trim()?.split(";");
  }, [order]);
  const [showPODModal, setShowPODModal] = useState(false);
  const handleOpenPODModal = () => {
    setShowPODModal(true);
  };
  const handleClosePODModal = () => {
    setShowPODModal(false);
  };

  const [parcelImageModalOpen, setParcelImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState();
  const handleParcelImageClick = async (e) => {
    setSelectedImage(e.currentTarget.dataset.imageId);
    setParcelImageModalOpen(true);
  };
  const handleCloseParcelImage = () => {
    setParcelImageModalOpen(false);
    setSelectedImage();
  };

  return (
    <>
      <FullPageParcelImageModal
        show={parcelImageModalOpen}
        isDemoAccount={isDemoAccountBool}
        handleCloseModal={handleCloseParcelImage}
        // imageURLToFetch={imageURLToFetch}
        selectedImage={selectedImage}
        barcode={order?.isManual ? "Manual" : originalOrderData?.invoiceNumber}
        caption={() => {
          return <div>POD Image</div>;
        }}
      />
      <PodModal
        show={showPODModal}
        handleCloseModal={handleClosePODModal}
        showPodComment={false}
        podName={isDemoAccountBool ? "Finroy" : originalOrderData?.pod}
        prependDataURI={!isDemoAccountBool}
        podSignature={
          isDemoAccountBool
            ? cdkDemoSignatureFinroy
            : originalOrderData?.signature
        }
        orderLevel={false} // true for order level POD in order tracking history, false for package/barcode level POD in parcel tracking history
        barcode={order?.isManual ? "Manual" : originalOrderData?.invoiceNumber}
        mainInvoice={originalOrderData?.invoiceNumber}
        downloadStringOverride={`Signature for order ${
          order?.isManual ? "Manual Stop" : originalOrderData?.invoiceNumber
        } (${positionName})`}
      />
      {order?.assignedTo ? (
        <>
          <h5 className="mt-4">Status</h5>
          <div className="panel-flat p-2 m-2">
            {order?.status === dedicatedOrderStatuses.completed ? (
              // true ?
              <div className="row">
                <div className="col-8">
                  <div>Completed - {dateDisplay}</div>
                  <div>
                    Signed By:
                    <br />
                    {isDemoAccountBool
                      ? "Finroy"
                      : (originalOrderData.pod ?? "N/A")}
                  </div>
                  <div>
                    Signature:
                    <br />
                    {isDemoAccountBool ||
                    (originalOrderData.signature &&
                      originalOrderData.signature.trim() !== "") ? (
                      <div
                        onClick={handleOpenPODModal}
                        className="order-details-card-pod-signature-container"
                      >
                        <img
                          className="order-details-card-pod-signature-image"
                          src={
                            isDemoAccountBool
                              ? cdkDemoSignatureFinroy
                              : "data:image/svg+xml;base64," +
                                originalOrderData?.signature
                          }
                          alt="Signature"
                        />
                        <BsZoomIn className="pod-signature-zoom-icon" />
                      </div>
                    ) : (
                      "No Signature Found"
                    )}
                  </div>
                </div>
                <div className="col-4">
                  <div className="panel-flat p-2">
                    <div className="font-500 border-bottom mb-2">Images</div>
                    <ul className="order-details-card-order-history-list p-0">
                      {attachmentIDs ? (
                        attachmentIDs.map((attachmentID) => {
                          return (
                            <button
                              key={attachmentID}
                              className="parcel-tracking-image-button px-3"
                              data-image-id={attachmentID}
                              onClick={handleParcelImageClick}
                            >
                              <li>{attachmentID}</li>
                            </button>
                          );
                        })
                      ) : (
                        <div>No Images Found</div>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  height: "100px",
                  display: "grid",
                  placeContent: "center",
                  fontWeight: "600",
                }}
              >
                {order?.status === dedicatedOrderStatuses.submitted
                  ? "Pending Completion"
                  : "Not Submitted"}
              </div>
            )}
          </div>
        </>
      ) : null}
    </>
  );
};

export const DedicatedOrderDetails = ({
  orderTerminology = "Order",
  order,
  show,
  onClose,
  position,
}) => {
  // const orderTermLowerCase = orderTerminology?.toLowerCase() ?? "order";
  // console.log("details panel order", order);
  const originalOrderData = order?.originalData;
  // const [viewByField, setViewByField] = useLocalStorage({ //don't like it stored personally
  //   key: localStorageVariables.viewDedicatedOrderDetailsByField,
  //   initialValue: false,
  // });
  const [viewByField, setViewByField] = useState(false);
  const [showBasedOnShipToValues, setShowBasedOnShipToValues] = useState(false);
  const toggleToShipToValues = () => {
    setShowBasedOnShipToValues(true);
  };
  const toggleToNormalCustomerValues = () => {
    setShowBasedOnShipToValues(false);
  };

  // console.log(order);

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton style={{ alignItems: "center" }}>
        <Modal.Title className="w-100">
          <div className="d-flex justify-content-between">
            {!order?.isManual ? (
              <>
                Invoice {originalOrderData?.invoiceNumber}
                {position ? ` for ${position.positionName}` : ""}
                <div className="text-right">
                  <div
                    className="btn-group"
                    style={{ boxShadow: "0px 0px 7px rgb(0,0,0,.2)" }}
                  >
                    <button
                      type="button"
                      className={"btn" + (!viewByField ? " btn-primary" : "")}
                      onClick={() => {
                        setViewByField(false);
                      }} // can also do this the e.target.name way, but if you click on something inside the button, e.target is that instead and won't have the button name. probably can disable pointer events on the things inside, not sure, decided to go this way.
                    >
                      Summary
                      {/* <BsCardList /> */}
                    </button>

                    <button
                      type="button"
                      className={"btn" + (viewByField ? " btn-primary" : "")}
                      onClick={() => {
                        setViewByField(true);
                      }}
                    >
                      All Fields
                      {/* <BsList /> */}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              `Manual Stop for ${position?.positionName}`
            )}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {order?.isManual ? (
          <div>
            {/* {console.log("All order fields - manual", order)} */}
            <div className="row">
              <div className="col-6">
                <div className="font-600">Address</div>
                {order?.address}, {order?.city}, {order?.state} {order?.zip}
              </div>
              <div className="col-6">
                <div className="font-600">Stop Type</div>
                {
                  Object.values(CXTStopTypes).find((CXTStopType) => {
                    return CXTStopType.value === order?.stopType;
                  })?.display
                }
              </div>
              <div className="col-6">
                <div className="font-600">Notes</div>
                {order?.notes}
              </div>
              <div className="col-6">
                <div className="font-600">Total Value</div>
                {order?.totalValue}
              </div>
            </div>
          </div>
        ) : (
          <div>
            {viewByField && originalOrderData ? (
              <div className="row">
                {Object.entries(originalOrderData).map((entry) => {
                  //Hiding these fields as they are not actually original order data fields
                  return (
                    ![
                      "positionId",
                      "signature",
                      "cdkPositionLinkId",
                      "pod",
                      "sequence",
                      "attachments",
                      "completedTime",
                      "positionDateTimeAdded",
                      "isHidden",
                      "isImported",
                    ].includes(entry[0]) && (
                      <div key={entry[0]} className="col-4">
                        <div className="font-600">{entry[0]}</div>
                        <div>
                          {entry[1] ?? <span className="font-600">-</span>}
                        </div>
                      </div>
                    )
                  );
                })}
              </div>
            ) : (
              <>
                {/* <div className="row">
                <div className="col-6">
                  {originalOrderData?.customerName} -{" "}
                  {originalOrderData?.custNo}
                  <div style={{ fontSize: ".75rem" }}>
                    {originalOrderData?.customerName2}
                  </div>
                  <div className="">
                    {originalOrderData?.customerAddress}
                    <br />
                    {originalOrderData?.customerCSZ}
                    <br />
                  </div>
                </div>
                <div className="col-6">
                  <div>Ship To Information</div>
                  <div className="font-small">
                    {originalOrderData?.shipToLine1}
                    <br />
                    {originalOrderData?.shipToLine2}
                    <br />
                    {originalOrderData?.shipToLine3}
                    <br />
                    {originalOrderData?.shipToLine4}
                  </div>
                </div>
              </div> */}
                <div className="row">
                  <div className="col-6">
                    <div className="d-flex justify-content-between align-items-start">
                      <h5>Customer Details:</h5>
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className="btn btn-secondary btn-small"
                          onClick={toggleToNormalCustomerValues}
                        >
                          CSZ
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-small"
                          onClick={toggleToShipToValues}
                        >
                          ShipTo
                        </button>
                      </div>
                    </div>
                    {showBasedOnShipToValues ? (
                      <>
                        1: {originalOrderData?.shipToLine1}
                        <br />
                        2: {originalOrderData?.shipToLine2}
                        <br />
                        3: {originalOrderData?.shipToLine3}
                        <br />
                        4: {originalOrderData?.shipToLine4}
                      </>
                    ) : (
                      <>
                        {originalOrderData?.customerName}
                        {originalOrderData?.custNo ? (
                          <span className="text-note">
                            {" "}
                            - {originalOrderData.custNo}
                          </span>
                        ) : (
                          ""
                        )}
                        {originalOrderData?.customerName2 &&
                        originalOrderData.customerName2.trim() !== "" ? (
                          <>
                            <br />

                            <span className="font-small text-note">
                              {originalOrderData.customerName2}
                            </span>
                          </>
                        ) : (
                          ""
                        )}
                        <br />
                        {originalOrderData?.customerAddress}
                        <br />
                        {originalOrderData?.customerCSZ}
                      </>
                    )}
                  </div>
                  <div className="col-6">
                    <h5>{orderTerminology} Info</h5>
                    <span className="font-600">PO Number:</span>{" "}
                    {originalOrderData?.poNumber}
                    <br />
                    <span className="font-600">Total Parts Sale:</span>{" "}
                    {originalOrderData?.totalPartsSale}
                    <br />
                    <span className="font-600">Invoice Sale Type:</span>{" "}
                    {originalOrderData?.invSaleType}
                    <br />
                    <span className="font-600">Number Of Parts:</span>{" "}
                    {originalOrderData?.numberOfParts}
                    {originalOrderData?.id && (
                      <PartNumberDisplay cdkOrderId={originalOrderData.id} />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        <DedicatedStatusPanel
          order={order}
          positionName={position?.positionName}
        />
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};
